import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Slider,
  Snackbar,
  Alert,
} from "@mui/material";
import { Grid2 as Grid } from "@mui/material";
import Cropper from "react-easy-crop";
import getCroppedImg from "./talent/components/cropUtils";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import { CircularProgress } from "@mui/material";

// -------------------- Field Config (per section) --------------------
const FIELD_CONFIG = {
  Name: [
    { name: "firstName", label: "First Name", type: "text" },
    { name: "middleName", label: "Middle Name", type: "text" },
    { name: "lastName", label: "Last Name", type: "text" },
  ],

  "Profile Picture": [
    { name: "profileImageFile", label: "Profile Image", type: "file" },
  ],

  Location: [
    {
      name: "location",
      label: "Location",
      type: "select",
      optionsKey: "locations",
    },
  ],

  Measurements: [
    { name: "heightStart", label: "Height (Feet)", type: "number" },
    { name: "heightEnd", label: "Height (Inches)", type: "number" },
    { name: "weight", label: "Weight (lbs)", type: "number" },
    {
      name: "eyeColor",
      label: "Eye Color",
      type: "select",
      optionsKey: "eyeColors",
    },
    {
      name: "hairColor",
      label: "Hair Color",
      type: "select",
      optionsKey: "hairColors",
    },
    { name: "bust", label: "Bust", type: "number" },
    { name: "waist", label: "Waist", type: "number" },
    { name: "hips", label: "Hips", type: "number" },
    { name: "dress", label: "Dress", type: "number" },
    { name: "shoe", label: "Shoe Size", type: "number" },
  ],

  "Vocal Range": [
    {
      name: "vocalRangeID",
      label: "Vocal Range",
      type: "select",
      optionsKey: "vocalRanges",
    },
  ],

  "Vocal Type": [
    {
      name: "vocalTypeIds",
      label: "Vocal Types",
      type: "multiselect",
      optionsKey: "vocalTypes",
    },
  ],

  Affiliations: [
    {
      name: "affiliationIds",
      label: "Affiliations",
      type: "multiselect",
      optionsKey: "affiliations",
    },
  ],

  Representation: [
    { name: "headingName", label: "Heading", type: "text" },
    { name: "nameofCompany", label: "Company Name", type: "text" },
    { name: "representativeName", label: "Representative Name", type: "text" },
    { name: "representativeContact", label: "Contact Info", type: "text" },
  ],

  "Resume Text": [
    { name: "resumeTextHeading", label: "Title", type: "text" },
    { name: "resumeTextDetails", label: "Details", type: "textarea" },
  ],

  Credits: [
    { name: "title", label: "Project Title", type: "text" },
    { name: "role", label: "Role / Character", type: "text" },
    { name: "company", label: "Director / Production", type: "text" },
    { name: "url", label: "URL", type: "text" },
  ],
};

const EditProfilePrompt = ({
  open,
  onClose,
  section,
  initialData,
  onSave,
  talentData,
}) => {
  const [formData, setFormData] = useState(initialData || {});
  const [eyeColors, setEyeColors] = useState([]);
  const [hairColors, setHairColors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vocalRanges, setVocalRanges] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [vocalTypes, setVocalTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const userId = localStorage.getItem("userId");
  const [uploading, setUploading] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Profile image cropper state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState(null); // { file, url }
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(3 / 4); // Portrait-ish for profile

  // -------------------- Prefill --------------------
  useEffect(() => {
    if (!open) return;

    // ⭐ IMPORTANT: Credits should NOT merge with talentData
    if (section === "Credits") {
      setFormData({
        resumeSectionDetailId: initialData?.resumeSectionDetailId || 0,
        resumeSectionId: initialData?.resumeSectionId,
        title: initialData?.title || "",
        role: initialData?.role || "",
        company: initialData?.company || "",
        url: initialData?.url || "", //  THIS fixes the issue
      });
      return;
    }

    // Default behavior for all other sections (unchanged)
    setFormData({ ...(talentData || {}), ...(initialData || {}) });

    if (section === "Measurements") {
      fetchEyeColors();
      fetchHairColors();
    }

    if (section === "Location") fetchLocations();
    if (section === "Vocal Range") fetchVocalRanges();
    if (section === "Affiliations") fetchAffiliations();
    if (section === "Vocal Type") fetchVocalTypes();
  }, [open, section, initialData, talentData]);

  // Cleanup local blob URLs when dialog closes
  useEffect(() => {
    if (!open) {
      if (cropImage?.url) {
        URL.revokeObjectURL(cropImage.url);
        setCropImage(null);
      }
      if (profilePreviewUrl) {
        URL.revokeObjectURL(profilePreviewUrl);
        setProfilePreviewUrl("");
      }
      setCropModalOpen(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setErrors({});
    }
  }, [open, cropImage, profilePreviewUrl]);

  // -------------------- Fetch helpers --------------------
  const fetchEyeColors = async () => {
    try {
      const response = await api.get(ENDPOINTS.getEyeColors());
      const result = response.data;
      if (Array.isArray(result.data)) {
        setEyeColors(result.data.filter((c) => c.activeFlag));
      }
    } catch (err) {
      console.error("Error fetching eye colors:", err);
    }
  };

  const fetchHairColors = async () => {
    try {
      const response = await api.get(ENDPOINTS.getHairColors());
      const result = response.data;
      if (Array.isArray(result.data)) {
        setHairColors(result.data.filter((c) => c.activeFlag));
      }
    } catch (err) {
      console.error("Error fetching hair colors:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get(ENDPOINTS.getLocations());
      const data = response.data;
      setLocations(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  const fetchVocalRanges = async () => {
    try {
      const response = await api.get(ENDPOINTS.getVocalRanges());
      const data = response.data;
      setVocalRanges(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Error fetching vocal ranges:", err);
    }
  };

  const fetchVocalTypes = async () => {
    try {
      const response = await api.get(ENDPOINTS.getVocalTypes());
      const data = response.data;
      setVocalTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching vocal types:", err);
    }
  };

  const fetchAffiliations = async () => {
    try {
      const response = await api.get(ENDPOINTS.getUnions());
      const data = response.data;
      // endpoint shape can be either data or data.data; support both
      const arr = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      setAffiliations(arr);
    } catch (err) {
      console.error("Error fetching affiliations:", err);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const match =
      url?.match(/[?&]v=([^&]+)/) || url?.match(/youtu\.be\/([^?&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : "";
  };

  // -------------------- Validation --------------------
  const validate = () => {
    const newErrors = {};

    if (section === "Resume Text") {
      const title = formData.resumeTextHeading?.trim() || "";
      if (!title) {
        newErrors.resumeTextHeading = "Title is required";
      } else if (title.length > 15) {
        newErrors.resumeTextHeading = "Title must be 15 characters or less";
      }
    }

    if (section === "Credits") {
      if (!formData.title?.trim())
        newErrors.title = "Project Title is required";
      if (!formData.role?.trim()) newErrors.role = "Role/Character is required";
      if (!formData.company?.trim())
        newErrors.company = "Director/Production is required";
    }

    if (section === "Measurements") {
      if (
        formData.heightStart &&
        (isNaN(formData.heightStart) || parseFloat(formData.heightStart) > 9)
      ) {
        newErrors.heightStart = "Height (Feet) must be a number less than 9";
      }
      if (
        formData.heightEnd &&
        (isNaN(formData.heightEnd) || parseFloat(formData.heightEnd) > 11)
      ) {
        newErrors.heightEnd = "Height (Inches) must be between 0 and 11";
      }
      if (
        formData.weight &&
        (isNaN(formData.weight) || parseFloat(formData.weight) > 500)
      ) {
        newErrors.weight = "Weight must be a number less than 500";
      }

      ["bust", "waist", "hips", "dress", "shoe"].forEach((field) => {
        if (formData[field] && isNaN(formData[field])) {
          newErrors[field] = `${field
            .charAt(0)
            .toUpperCase()}${field.slice(1)} must be a number`;
        }
      });
    }

    if (section === "Vocal Type") {
      if (!formData.vocalTypeIds?.length) {
        newErrors.vocalTypeIds = "Please select at least one vocal type.";
      }
    }

    if (section === "Affiliations") {
      if (!formData.affiliationIds?.length) {
        newErrors.affiliationIds = "Please select at least one affiliation.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------- Profile Update --------------------
  const handleProfileUpdate = async () => {
    if (!validate()) return;

    // Map API response fields to required payload fields
    const email = talentData.userEmail || talentData.email || "";
    const primaryContactNumber =
      talentData.primaryContactNumber ||
      talentData.phoneNumber ||
      talentData.PhoneNumber ||
      "";
    const stateCode =
      (section === "Location"
        ? formData.location
        : talentData.locationCode ||
          talentData.stateCode ||
          talentData.StateCode ||
          "") || "";
    const sex = talentData.sex || talentData.Sex || talentData.gender || "";

    const eye = eyeColors.find((c) => c.eyeColorName === formData.eyeColor);
    const hair = hairColors.find((c) => c.hairColorName === formData.hairColor);

    const vocalRangeID =
      section === "Vocal Range"
        ? formData.vocalRangeID
        : talentData.vocalRangeID;

    // FINAL PAYLOAD with required fields ALWAYS included
  const payload = {
  ...talentData, // Preserve everything from the GET response

  userId: talentData.userId ?? userId,

  // Updated fields
  firstName: formData.firstName ?? talentData.firstName,
  middleName: formData.middleName ?? talentData.middleName,
  lastName: formData.lastName ?? talentData.lastName,

  email,
  sex,
  stateCode,
  primaryContactNumber,

  // Measurements
  weight:
    section === "Measurements"
      ? Number(formData.weight)
      : talentData.weight,
  heightStart:
    section === "Measurements"
      ? Number(formData.heightStart)
      : talentData.heightStart,
  heightEnd:
    section === "Measurements"
      ? Number(formData.heightEnd)
      : talentData.heightEnd,

  // Colors
  eyeColorCode: eye?.eyeColorCode ?? talentData.eyeColorCode,
  hairColorCode: hair?.hairColorCode ?? talentData.hairColorCode,

  // Vocal
  vocalRangeID,

  // Body
  dress: formData.dress ?? talentData.dress,
  bust: formData.bust ?? talentData.bust,
  waist: formData.waist ?? talentData.waist,
  hips: formData.hips ?? talentData.hips,
  shoe: formData.shoe ?? talentData.shoe,
};

    try {
      const response = await api.post(ENDPOINTS.updateTalentProfile(), payload);
      const result = response.data;

      if (
  response.status === 200 &&
  (result.success || result.status === "Ok")
) {
  onSave(result.data ?? payload);

  onClose();

  window.dispatchEvent(new Event("talent-data-updated"));
} else {
        setToast({
          open: true,
          message: "Failed to update profile",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Network error updating profile", err);
      setToast({
        open: true,
        message: "Network error updating profile",
        severity: "error",
      });
    }
  };

  // -------------------- PROFILE PICTURE UPLOAD --------------------
  const handleProfileImageUpload = async () => {
    const file = formData.profileImageFile;

    if (!file) {
      setToast({
        open: true,
        message: "Please select an image.",
        severity: "error",
      });
      return;
    }

    const uploadForm = new FormData();
    uploadForm.append("MediaId", 0);
    uploadForm.append("UserId", talentData.userId ?? userId);
    uploadForm.append("MediaType", "PROFILE");
    uploadForm.append("MediaStyle", "");
    uploadForm.append("MediaTitle", "");
    uploadForm.append("PicSort", 0);
    uploadForm.append("ActiveFlag", true);
    uploadForm.append("UserBy", talentData.userId ?? userId);
    uploadForm.append("MediaFile", file);
    uploadForm.append("MediaLink", "null");

    try {
      setUploading(true); // 🔄 START LOADING

      const res = await api.post(ENDPOINTS.addOrUpdateUserMedia(), uploadForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result = res.data;

      if (res.status === 200 && (result.success || result.mediaId)) {
        setToast({
          open: true,
          message: "Profile picture updated successfully!",
          severity: "success",
        });

        // 🔁 Trigger refresh in TalentHome
        window.dispatchEvent(new Event("talent-data-updated"));

        onClose();
      } else {
        setToast({
          open: true,
          message: "Failed to upload profile picture.",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Profile image upload error", err);
      setToast({
        open: true,
        message: "Network error uploading image.",
        severity: "error",
      });
    } finally {
      setUploading(false); //  STOP LOADING
    }
  };

  // -------------------- Handle Save --------------------
  const handleSave = async () => {
    // Special flow for Profile Picture
    if (section === "Profile Picture") {
      await handleProfileImageUpload();
      return;
    }

    if (!validate()) return;

    if (["Name", "Location", "Measurements", "Vocal Range"].includes(section)) {
      await handleProfileUpdate();
      return;
    }

    if (section === "Resume Text") {
      const isNew = isAddingNew || !formData.resumeTextDataId;
      const payload = {
        resumeTextDataId: isNew ? 0 : formData.resumeTextDataId,
        resumeSectionId: Number(formData.resumeSectionId || 0),
        userId,
        isInternal: 1,
        resumeTextHeading: formData.resumeTextHeading,
        resumeTextDetails: formData.resumeTextDetails || "",
        sortOrder: formData.sortOrder || 0,
        userBy: userId,
        activeFlag: true,
      };
      try {
        const resp = await api.post(ENDPOINTS.addOrUpdateResumeText(), payload);
        const result = resp.data;
        if (result.success) {
          onClose();
          if (window?.dispatchEvent) {
            window.dispatchEvent(
              new CustomEvent("resume-snackbar", {
                detail: {
                  message: isNew ? "New section added" : "Section updated",
                  severity: "success",
                },
              }),
            );
          }
          window.dispatchEvent(new Event("talent-data-updated"));
        } else {
          console.error("Failed to save:", result.message);
          setToast({
            open: true,
            message: "Failed to save resume section",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error saving resume text:", error);
        setToast({
          open: true,
          message: "Error saving resume text",
          severity: "error",
        });
      }
    } else if (section === "Credits") {
      const payload = {
        resumeSectionDetailId: formData.resumeSectionDetailId || 0,
        resumeSectionId: formData.resumeSectionId,
        title: formData.title,
        role: formData.role,
        company: formData.company,
        url: formData.url,
        userId,
        userBy: userId,
        sortOrder: 0,
        activeFlag: true,
      };

      try {
        const resp = await api.post(
          ENDPOINTS.addOrUpdateResumeSectionDetails(),
          payload,
        );
        const result = resp.data;

        if (
          resp.status === 200 &&
          (result.resumeSectionDetailId || result.success)
        ) {
          onSave(payload);
          onClose();
          setToast({
            open: true,
            message: "Project updated successfully",
            severity: "success",
          });
          window.dispatchEvent(new Event("talent-data-updated"));
        } else {
          setToast({
            open: true,
            message: "Failed to update project",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error saving project:", error);
        setToast({
          open: true,
          message: "Something went wrong while saving project",
          severity: "error",
        });
      }
    } else if (section === "Representation") {
      const payload = {
        userId,
        headingName: formData.headingName,
        nameofCompany: formData.nameofCompany || "",
        representativeContact: formData.representativeContact || "",
        representativeName: formData.representativeName || "",
      };
      try {
        const resp = await api.post(
          ENDPOINTS.addOrUpdateRepresentation(),
          payload,
        );
        const result = resp.data;
        if (
          resp.status === 200 &&
          (result.success ||
            result.message === "Vocal types updated successfully")
        ) {
          onSave();
          onClose();
          setToast({
            open: true,
            message: "Representation info saved",
            severity: "success",
          });
        } else {
          setToast({
            open: true,
            message: "Failed to save representation info",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error saving representation:", error);
        setToast({
          open: true,
          message: "Something went wrong while saving representation",
          severity: "error",
        });
      }
    } else if (section === "Vocal Type") {
      if (!formData.vocalTypeIds?.length) {
        setErrors({
          vocalTypeIds: "Please select at least one vocal type.",
        });
        return;
      }

      const payload = {
        userId,
        userBy: userId,
        vocalTypeIds: formData.vocalTypeIds,
      };
      try {
        const resp = await api.post(ENDPOINTS.addOrUpdateVocalTypes(), payload);
        const result = resp.data;
       if (resp.status === 200 && result.message.includes("successfully")) {
  setErrors({});
  onSave(payload);
  onClose();
} else {
          setToast({
            open: true,
            message: "Failed to save vocal types",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error saving vocal types:", error);
        setToast({
          open: true,
          message: "Network error while saving vocal types",
          severity: "error",
        });
      }
    } else if (section === "Affiliations") {
      if (!formData.affiliationIds?.length) {
        setErrors({
          affiliationIds: "Please select at least one affiliation.",
        });
        return;
      }
      const payload = {
        userId,
        userBy: userId,
        affiliationIds: formData.affiliationIds,
      };
      try {
        const resp = await api.post(
          ENDPOINTS.addOrUpdateAffiliations(),
          payload,
        );
        const result = resp.data;
        if (resp.status === 200 && result.success) {
  onSave(payload);
  onClose();

  window.dispatchEvent(new Event("talent-data-updated"));
} else {
          setToast({
            open: true,
            message: "Failed to save affiliations",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error saving affiliations:", error);
        setToast({
          open: true,
          message: "Network error while saving affiliations",
          severity: "error",
        });
      }
    } else {
      // For sections like custom ones
      setErrors({});
      onSave(formData);
      onClose();
    }
  };

  // -------------------- Options helper for selects --------------------
  const getOptionsForField = (field) => {
    switch (field.optionsKey) {
      case "locations":
        return locations.map((loc) => ({
          value: loc.locationCode,
          label: loc.locationCity
            ? `${loc.locationCity}, ${loc.locationState}`
            : loc.locationState,
        }));
      case "vocalRanges":
        return vocalRanges.map((r) => ({
          value: r.vocalRangeId,
          label: r.vocalRangeName,
        }));

      case "affiliations":
        return affiliations.map((a) => ({
          value: a.affiliationId ?? a.unionId,
          label: a.affiliationName ?? a.unionName,
        }));
      case "vocalTypes":
        return vocalTypes.map((v) => ({
          value: v.vocalTypeId,
          label: v.vocalTypeName,
        }));
      case "eyeColors":
        return eyeColors.map((c) => ({
          value: c.eyeColorName,
          label: c.eyeColorName,
        }));
      case "hairColors":
        return hairColors.map((c) => ({
          value: c.hairColorName,
          label: c.hairColorName,
        }));
      default:
        return [];
    }
  };

  const fields = FIELD_CONFIG[section] || [];

  const existingProfileImage =
    profilePreviewUrl ||
    talentData?.profileImageUrl ||
    talentData?.talentImage ||
    "";

  // -------------------- UI --------------------
  return (
    <>
      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Main Dialog */}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {section === "Resume Text" &&
          (!formData.resumeTextDataId || formData.resumeTextDataId === 0)
            ? "Add Resume Text Section"
            : `Edit ${section}`}
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Special handling for profile picture */}
          {section === "Profile Picture" && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ mb: 1 }}>
                Upload a new profile image.
              </Typography>

              <Button variant="outlined" component="label" sx={{ mb: 2 }}>
                Choose Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  //  Allowed types
  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",
  ];

  // ❌ Type validation
  if (!allowedTypes.includes(file.type)) {
    setToast({
      open: true,
      message: "Only JPG, PNG, or WEBP images are allowed.",
      severity: "error",
    });
    return;
  }

  // ❌ Size validation (5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    setToast({
      open: true,
      message: "Image must be less than 5MB.",
      severity: "error",
    });
    return;
  }

  //  Validate dimensions
  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
  const result = { width: img.width, height: img.height, url };
  URL.revokeObjectURL(url); //  FIX
  resolve(result);
};

      img.onerror = reject;
      img.src = url;
    });

  let dimensions;
  try {
    dimensions = await getImageDimensions(file);
  } catch {
    setToast({
      open: true,
      message: "Invalid image file.",
      severity: "error",
    });
    return;
  }

  const { width, height } = dimensions;

  // ❌ Minimum dimension check
  if (width < 300 || height < 400) {
    URL.revokeObjectURL(dimensions.url);

    setToast({
      open: true,
      message: "Image must be at least 300x400 pixels.",
      severity: "error",
    });
    return;
  }

  //  Auto compress image (canvas)
  const compressImage = (file, quality = 0.8, maxWidth = 1200) =>
    new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, maxWidth / img.width);

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };

      reader.readAsDataURL(file);
    });

  let finalFile = file;

  try {
    finalFile = await compressImage(file);
  } catch {
    // fallback to original file if compression fails
    finalFile = file;
  }

  // cleanup old preview
  if (profilePreviewUrl) {
    URL.revokeObjectURL(profilePreviewUrl);
  }

 const previewUrl = URL.createObjectURL(finalFile);

setFormData((prev) => ({
  ...prev,
  profileImageFile: finalFile,
}));

setProfilePreviewUrl(previewUrl);

const cropUrl = URL.createObjectURL(finalFile);
setCropImage({ file: finalFile, url: cropUrl });
setCropModalOpen(true);
}}
                />
              </Button>

              {(profilePreviewUrl ||
                talentData?.profileImageUrl ||
                talentData?.talentImage) && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Preview:
                  </Typography>
                  <img
                    src={
                      profilePreviewUrl ||
                      talentData?.profileImageUrl ||
                      talentData?.talentImage
                    }
                    alt="Profile Preview"
                    style={{
                      width: 200,
                      height: "auto",
                      borderRadius: 6,
                      objectFit: "containc",
                    }}
                  />
                </Box>
              )}
              {/* {profilePreviewUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1.5 }}
                  onClick={() => {
                    const file = formData.profileImageFile;
                    if (!file) return;

                    const url = URL.createObjectURL(file);
                    setCropImage({ file, url });
                    setCropModalOpen(true);
                  }}
                >
                  Crop Image
                </Button>
              )} */}
            </Box>
          )}

          {/* Generic dynamic fields */}
          {section !== "Profile Picture" && fields.length > 0 && (
            <>
              {fields.map((field) => {
                const value =
                  formData[field.name] ??
                  (field.type === "multiselect" ? [] : "");
                const errorText = errors[field.name];

                if (field.type === "select" || field.type === "multiselect") {
                  const options = getOptionsForField(field);
                  const multiple = field.type === "multiselect";

                  return (
                    <FormControl
                      key={field.name}
                      fullWidth
                      margin="normal"
                      size="small"
                      error={!!errorText}
                    >
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        multiple={multiple}
                        label={field.label}
                        value={value}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: e.target.value,
                          }))
                        }
                        renderValue={(selected) => {
                          if (!multiple) {
                            const opt = options.find(
                              (o) => o.value === selected,
                            );
                            return opt?.label || "";
                          }
                          const arr = selected || [];
                          return options
                            .filter((o) => arr.includes(o.value))
                            .map((o) => o.label)
                            .join(", ");
                        }}
                      >
                        {options.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {multiple && (
                              <Checkbox
                                checked={value.indexOf(opt.value) > -1}
                              />
                            )}
                            <ListItemText primary={opt.label} />
                          </MenuItem>
                        ))}
                      </Select>
                      {errorText && (
                        <Typography color="error" variant="caption">
                          {errorText}
                        </Typography>
                      )}
                    </FormControl>
                  );
                }

                if (field.type === "textarea") {
                  return (
                    <TextField
                      key={field.name}
                      fullWidth
                      label={field.label}
                      value={value}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      multiline
                      minRows={4}
                      margin="normal"
                      error={!!errorText}
                      helperText={errorText}
                    />
                  );
                }

                if (field.type === "number") {
                  return (
                    <TextField
                      key={field.name}
                      fullWidth
                      label={field.label}
                      type="number"
                      value={value}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      margin="normal"
                      error={!!errorText}
                      helperText={errorText}
                    />
                  );
                }

                // default text field
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.label}
                    value={value}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    margin="normal"
                    error={!!errorText}
                    helperText={errorText}
                  />
                );
              })}
            </>
          )}

          {/* Fallback if no config exists */}
          {fields.length === 0 && section !== "Profile Picture" && (
            <Typography color="text.secondary">
              There are no editable fields configured for <b>{section}</b> yet.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {uploading ? "Uploading..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cropper Modal for Profile Picture */}
      <Dialog open={cropModalOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Crop Profile Image</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
            px: 3,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 400,
              aspectRatio: aspect,
              backgroundColor: "#000",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Cropper
              image={cropImage?.url}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={(_, area) => setCroppedAreaPixels(area)}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: 400, mt: 3 }}>
            {/* Aspect Ratio Selector */}
            <Typography gutterBottom>Aspect Ratio</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={aspect}
              onChange={(e) => setAspect(parseFloat(e.target.value))}
              sx={{ mb: 2 }}
            >
              <MenuItem value={1 / 1}>1:1 (Square)</MenuItem>
              <MenuItem value={3 / 4}>3:4 (Portrait)</MenuItem>
              <MenuItem value={4 / 3}>4:3 (Landscape)</MenuItem>
              <MenuItem value={16 / 9}>16:9 (Widescreen)</MenuItem>
            </TextField>

            <Typography gutterBottom>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, v) => setZoom(v)}
            />

            <Typography gutterBottom sx={{ mt: 2 }}>
              Rotate
            </Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_, v) => setRotation(v)}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCropModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!cropImage || !croppedAreaPixels) return;
              try {
                const { blob } = await getCroppedImg(
                  cropImage.url,
                  croppedAreaPixels,
                  rotation,
                );
                const croppedFile = new File([blob], cropImage.file.name, {
                  type: blob.type,
                });

                // cleanup old preview if any
                if (profilePreviewUrl) {
                  URL.revokeObjectURL(profilePreviewUrl);
                }

                const preview = URL.createObjectURL(croppedFile);
                setProfilePreviewUrl(preview);

                setFormData((prev) => ({
                  ...prev,
                  profileImageFile: croppedFile,
                }));
              } catch (err) {
                console.error("Crop error:", err);
                setToast({
                  open: true,
                  message: "Error while cropping image",
                  severity: "error",
                });
              } finally {
                if (cropImage?.url) {
                  URL.revokeObjectURL(cropImage.url);
                }
                setCropImage(null);
                setCropModalOpen(false);
                setZoom(1);
                setRotation(0);
              }
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditProfilePrompt;
